import { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { testPaymentMethod } from '../../services/paymentServices';
import { AlertCircle, CreditCard, Check } from 'lucide-react';
import StripeCardInputWrapper from '../../components/payments/StripeCardInput';

export default function PaymentsSettings() {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [billingEmail, setBillingEmail] = useState('');
  const [lastPaymentTest, setLastPaymentTest] = useState<any>(null);
  const [paymentMethodStatus, setPaymentMethodStatus] = useState<'valid' | 'invalid' | 'none'>('none');
  const [isCardReady, setIsCardReady] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [savingEmail, setSavingEmail] = useState(false);
  const [emailSaved, setEmailSaved] = useState(false);
  const [companyData, setCompanyData] = useState<any>(null);

  useEffect(() => {
    if (!currentUser?.companyId) {
      return;
    }
    loadPaymentConfig();
  }, [currentUser]);

  const loadPaymentConfig = async () => {
    if (!currentUser?.companyId) return;

    try {
      const companyRef = doc(db, 'companies', currentUser.companyId);
      const companySnap = await getDoc(companyRef);
      
      if (companySnap.exists()) {
        const data = companySnap.data();
        setBillingEmail(data.billingEmail || '');
        setLastPaymentTest(data.lastPaymentTest || null);
        setPaymentMethodStatus(
          data.defaultPaymentMethod ? 
            (data.lastPaymentTest?.success ? 'valid' : 'invalid') : 
            'none'
        );
        setCompanyData(data);
      }
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
      setError('Erro ao carregar configurações de pagamento');
    }
  };

  const handleSaveEmail = async () => {
    if (!currentUser?.companyId) return;
    if (!billingEmail) {
      setError('Por favor, insira um email válido');
      return;
    }

    setSavingEmail(true);
    setEmailSaved(false);
    setError(null);

    try {
      const companyRef = doc(db, 'companies', currentUser.companyId);
      await updateDoc(companyRef, {
        billingEmail: billingEmail
      });
      setEmailSaved(true);
      setTimeout(() => setEmailSaved(false), 3000); // Remove o check após 3 segundos
    } catch (err) {
      console.error('Erro ao salvar email:', err);
      setError('Erro ao salvar email de faturamento');
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePaymentMethod = async (paymentMethod: any) => {
    if (!currentUser?.companyId) {
      setError('Usuário não está associado a uma empresa');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Testar o cartão com pagamento de 1€
      const testResult = await testPaymentMethod(
        currentUser.companyId,
        companyData?.stripeCustomerId || 'cus_test',
        paymentMethod.id
      );

      if (testResult.success) {
        // Buscar campanhas pausadas por pagamento
        const campaignsRef = collection(db, 'campaigns');
        const q = query(
          campaignsRef,
          where('companyId', '==', currentUser.companyId),
          where('status', '==', 'paused'),
          where('pausedReason', '==', 'payment_method_invalid')
        );
        
        const snapshot = await getDocs(q);
        const pendingCampaigns = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Se houver campanhas pendentes, processar pagamentos
        if (pendingCampaigns.length > 0) {
          for (const campaign of pendingCampaigns) {
            try {
              // Criar PaymentIntent para o valor pendente
              const response = await fetch('/api/stripe/create-payment-intent', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  amount: campaign.stats?.amountToPay || 0,
                  customerId: companyData?.stripeCustomerId,
                  campaignId: campaign.id
                })
              });

              if (response.ok) {
                const result = await response.json();
                if (result.success) {
                  // Atualizar status da campanha
                  await updateDoc(doc(db, 'campaigns', campaign.id), {
                    status: 'active',
                    pausedReason: null,
                    lastPaymentDate: new Date().toISOString(),
                    nextPaymentDate: getNextFriday().toISOString()
                  });
                }
              }
            } catch (err) {
              console.error(`Erro ao processar pagamento para campanha ${campaign.id}:`, err);
            }
          }
        }

        setSuccess('Cartão configurado com sucesso! Os pagamentos pendentes foram processados.');
        setPaymentMethodStatus('valid');
      } else {
        setError(testResult.error || 'Erro ao validar cartão');
        setPaymentMethodStatus('invalid');
      }
    } catch (err: any) {
      console.error('Erro ao configurar cartão:', err);
      setError(err.message || 'Erro ao configurar cartão');
      setPaymentMethodStatus('invalid');
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para calcular a próxima sexta-feira
  const getNextFriday = () => {
    const today = new Date();
    const friday = new Date(today);
    friday.setDate(today.getDate() + ((7 + 5 - today.getDay()) % 7 || 7));
    friday.setHours(0, 0, 0, 0);
    return friday;
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-6">Configurações de Pagamento</h1>

      {/* Status do Cartão */}
      {paymentMethodStatus !== 'none' && (
        <div className={`mb-6 p-4 rounded-lg ${
          paymentMethodStatus === 'valid' 
            ? 'bg-green-50 border border-green-200 text-green-700'
            : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          <div className="flex items-center gap-2">
            {paymentMethodStatus === 'valid' ? (
              <>
                <CreditCard className="h-5 w-5" />
                <span>Cartão de crédito válido e ativo</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5" />
                <span>Cartão de crédito inválido ou expirado. Por favor, atualize suas informações de pagamento.</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mensagens de Erro/Sucesso */}
      {(error || cardError) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error || cardError}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold mb-4">Configurações de Pagamento</h2>
        
        <p className="text-gray-600 mb-6">
          Para criar campanhas publicitárias, é necessário configurar um cartão de crédito 
          para processamento dos pagamentos semanais.
        </p>

        {/* Email para Faturamento */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email para faturamento
          </label>
          <div className="flex items-center gap-2">
            <input
              type="email"
              value={billingEmail}
              onChange={(e) => setBillingEmail(e.target.value)}
              className="flex-1 p-2 border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="seu@email.com"
            />
            <button
              onClick={handleSaveEmail}
              disabled={savingEmail}
              className={`px-4 py-2 rounded flex items-center gap-2 transition-colors ${
                savingEmail
                  ? 'bg-gray-300 cursor-not-allowed'
                  : emailSaved
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {savingEmail ? (
                'Salvando...'
              ) : emailSaved ? (
                <>
                  <Check className="h-4 w-4" />
                  Salvo!
                </>
              ) : (
                'Salvar'
              )}
            </button>
          </div>
        </div>

        {/* Cartão de Crédito */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Cartão de Crédito
          </label>
          {paymentMethodStatus === 'valid' ? (
            <div className="mb-4 p-4 border rounded bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-600">
                    •••• •••• •••• {companyData?.lastFourDigits || '****'}
                  </span>
                </div>
                <button
                  onClick={() => setPaymentMethodStatus('none')}
                  className="text-primary hover:text-primary-dark text-sm"
                >
                  Alterar cartão
                </button>
              </div>
            </div>
          ) : (
            <div className="mb-4 p-4 border rounded">
              <StripeCardInputWrapper
                onCardReady={setIsCardReady}
                onError={setCardError}
                onPaymentMethod={handlePaymentMethod}
              />
            </div>
          )}
        </div>

        {/* Histórico de Testes */}
        {lastPaymentTest && (
          <div className="mt-6 p-4 bg-gray-50 rounded">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Último teste de pagamento</h3>
            <div className="text-sm text-gray-600">
              <p>Data: {new Date(lastPaymentTest.date).toLocaleString()}</p>
              <p>Status: {lastPaymentTest.success ? 'Sucesso' : 'Falha'}</p>
              {lastPaymentTest.error && <p>Erro: {lastPaymentTest.error}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
